import React, { useEffect, useState } from "react";
import {
  Typography,
  Button,
  CardContent,
  CardActions,
  Grid,
  Card,
  Divider,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { getBulkEmails, sendBulkEmail, deleteBulkEmails } from "../api";
import Loader from "../components/common/Loader";
import EmailModal from "../components/EmailModal";
import EmailRecipientsModal from "../components/EmailRecipientsModal";
import DeleteModal from "../components/DeleteModal";

const getRecipientText = (item) => {
  const previewUsers = item.recipientPreviewUsers || [];
  const previewNames = previewUsers
    .map((user) => user.username || user.email || user.id)
    .filter(Boolean);

  const restCount = Math.max((item.recipientUsersCount || 0) - previewNames.length, 0);
  if (!previewNames.length && !restCount) return "-";

  return restCount
    ? `${previewNames.join(", ")} +${restCount}`
    : previewNames.join(", ");
};

const formatEvent = (event) => {
  const date = event.date
    ? new Date(event.date).toLocaleDateString([], {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;
  const parts = [event.name, date && `(${date})`, event.venueName && `@ ${event.venueName}`];
  return parts.filter(Boolean).join(" ");
};

export default function EmailsPage() {
  const [state, setState] = useState({
    data: undefined,
    open: false,
    selectedEmail: null,
  });

  useEffect(() => {
    (async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) return;

      if (user.role !== "admin") {
        setState((s) => ({ ...s, data: [], role: user.role, user }));
        return;
      }

      const data = await getBulkEmails();
      setState((s) => ({ ...s, data: data || [], role: user.role, user }));
    })();
  }, []);

  const handleCreate = () => {
    setState((s) => ({
      ...s,
      open: true,
      handleConfirm: async (body) => {
        const report = await sendBulkEmail({
          emailTitle: body.emailTitle,
          selectedEventIds: body.selectedEventIds || [],
        });

        const data = await getBulkEmails();
        setState((prevState) => ({
          ...prevState,
          data,
          sendReport: report,
        }));
      },
    }));
  };

  const handleClose = () => {
    setState((s) => ({ ...s, open: false, handleConfirm: undefined }));
  };

  const handleDelete = (item) => () => {
    setState((s) => ({
      ...s,
      deleteOpen: true,
      deleteName: item.title || item.subject || "Untitled Email",
      handleDeleteConfirm: async () => {
        await deleteBulkEmails({ createdAt: item.createdAt, title: item.title });
        setState((prev) => ({
          ...prev,
          deleteOpen: false,
          data: prev.data.filter((x) => x.id !== item.id),
        }));
      },
    }));
  };

  const handleDeleteClose = () => {
    setState((s) => ({ ...s, deleteOpen: false, deleteName: undefined, handleDeleteConfirm: undefined }));
  };

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{ maxWidth: "1600px", paddingTop: "80px", paddingX: 4 }}
        justifyContent="center"
      >
        {state.role === "admin" && (
          <Grid item xs={12}>
            <Button type="button" onClick={handleCreate} variant="contained">
              Create Email
            </Button>
          </Grid>
        )}
        {state.sendReport && (
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ backgroundColor: "#3a3349", color: "white", fontFamily: "monospace", whiteSpace: "pre-wrap", padding: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="secondary" gutterBottom>
                  Send Report
                </Typography>
                <Typography variant="body2" component="pre" sx={{ color: "#d8c8f4", fontFamily: "monospace", fontSize: "0.8rem" }}>
                  {[
                    `Venues targeted:     ${state.sendReport.venueIds?.length ?? "-"}`,
                    `Events matched:      ${state.sendReport.eventsMatched ?? "-"}`,
                    `Analytics items:     ${state.sendReport.analyticsItems ?? "-"}`,
                    `Unique users:        ${state.sendReport.uniqueUsers ?? "-"}`,
                    `Resolved users:      ${state.sendReport.resolvedUsers ?? "-"}`,
                    `Skipped (today):     ${state.sendReport.skippedDailyLimit ?? "-"}`,
                    `Email eligible:      ${state.sendReport.emailEligibleUsers ?? "-"}`,
                    `Stored:              ${state.sendReport.stored ?? "-"}`,
                    `Emails sent:         ${state.sendReport.sent ?? "-"}`,
                    `Failed:              ${state.sendReport.failed ?? "-"}`,
                    ...(state.sendReport.events?.length
                      ? ["", "Events:", ...state.sendReport.events.map((e) => `  - ${e.name || "(unnamed)"} (${e.date || "no date"}) @ ${e.venueName || "unknown venue"}`)]
                      : []),
                  ].join("\n")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {state.data ? (
          <Grid item xs={12}>
            <Typography
              variant="h6"
              sx={{ textAlign: "left", color: "white", marginBottom: "10px" }}
            >
              Emails
            </Typography>
            <Grid container spacing={3} justifyContent="center">
              {state.data.map((item) => (
                <Grid item xs={12} key={item.id} sx={{ display: "flex", justifyContent: "center" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      width: { xs: "100%", md: "70vw", lg: "65vw" },
                      maxWidth: "1100px",
                      backgroundColor: "#4b445b",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" color="secondary" gutterBottom>
                        {item.title || item.subject || "Untitled Email"}
                      </Typography>
                      <Divider sx={{ borderColor: "rgba(216, 200, 244, 0.2)", marginBottom: 1.5 }} />
                      <Typography
                        variant="body2"
                        color="secondary"
                        onClick={() => setState((s) => ({ ...s, selectedEmail: item }))}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        Sent to: {getRecipientText(item)}
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Opened: {item.openedCount ?? 0} / {item.recipientUsersCount ?? 0}
                        {item.recipientUsersCount
                          ? ` (${Math.round(((item.openedCount ?? 0) / item.recipientUsersCount) * 100)}%)`
                          : ""}
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Unsubscribed: {item.unsubscribedCount ?? 0} / {item.recipientUsersCount ?? 0}
                        {item.recipientUsersCount
                          ? ` (${Math.round(((item.unsubscribedCount ?? 0) / item.recipientUsersCount) * 100)}%)`
                          : ""}
                      </Typography>
                      <Typography variant="body2" color="secondary">
                        Sent at: {item.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      </Typography>
                      {item.selectedEvents?.length > 0 && (
                        <Typography variant="body2" color="secondary">
                          Targeted event(s): {item.selectedEvents.map(formatEvent).join(", ")}
                        </Typography>
                      )}
                      {item.targetEvents?.length > 0 && (
                        <Typography variant="body2" color="secondary">
                          Recipients qualified via: {item.targetEvents.map((e) => formatEvent({ name: e.name, date: e.date })).join(", ")}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end" }}>
                      <IconButton onClick={handleDelete(item)} sx={{ color: "#d8c8f4" }}>
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Loader />
        )}
      </Grid>
      {state.selectedEmail && (
        <EmailRecipientsModal
          emailId={state.selectedEmail.id}
          recipientCount={state.selectedEmail.recipientUsersCount}
          onClose={() => setState((s) => ({ ...s, selectedEmail: null }))}
        />
      )}
      {state.open && (
        <EmailModal
          user={state.user}
          handleConfirm={state.handleConfirm}
          handleClose={handleClose}
        />
      )}
      {state.deleteOpen && (
        <DeleteModal
          name={state.deleteName}
          handleConfirm={state.handleDeleteConfirm}
          handleClose={handleDeleteClose}
        />
      )}
    </>
  );
}
